import {
  resolveKeyMapper
} from "./chunk-4SQQRBMJ.js";
import {
  SubscribableImpl,
  ThreadInMemory,
  anyOf,
  createDebugName,
  isInitEvent,
  isoDateStrCompare,
  makeFilter,
  memoizedFn,
  rollingFilter
} from "./chunk-236RMLMX.js";

// src/query/divergences.ts
import { Logger } from "besonders-logger";
var { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO);
var queryDivergencesByPrev = memoizedFn("queryDivergencesByPrev", function queryConflictingByPrev(sourceThread) {
  DEBUG(`queryDivergencesByPrev<${sourceThread.nameAndSizeUntracked}>`);
  if (sourceThread.filters.includes("lastWriteWins")) WARN(`queryDivergencesByPrev on thread lastWriteWins`, sourceThread);
  const logsForNode = /* @__PURE__ */ new Map();
  const leafs = /* @__PURE__ */ new Set();
  VERBOSE("all applogs:", sourceThread.applogs);
  for (const log of sourceThread.applogs) {
    let prevLogs;
    if (log.pv) {
      prevLogs = log.pv && logsForNode.get(log.pv.toString());
      leafs.delete(log.pv.toString());
    }
    VERBOSE("traversing log", { log, prevLogs, leafs: Array.from(leafs) });
    logsForNode.set(log.cid, prevLogs ? [...prevLogs, log] : [log]);
    leafs.add(log.cid);
  }
  const divergences = Array.from(leafs).map((leafID) => {
    const thread = new ThreadInMemory(
      createDebugName({
        caller: "DivergenceLeaf",
        thread: sourceThread,
        pattern: `leaf: ${leafID}`
      }),
      logsForNode.get(leafID),
      sourceThread.filters,
      true
    );
    return { log: thread.latestLog, thread };
  });
  return divergences;
});

// src/query/entity-collection.ts
import { Logger as Logger2 } from "besonders-logger";
var { DEBUG: DEBUG2 } = Logger2.setup(Logger2.INFO);
function liveEntityCollection(thread, discoveryPattern, liveAttributes, opts) {
  return _liveEntityCollection(
    thread,
    discoveryPattern,
    liveAttributes,
    opts
  );
}
var _liveEntityCollection = memoizedFn(
  "liveEntityCollection",
  function liveEntityCollection2(thread, discoveryPattern, liveAttributes, opts) {
    DEBUG2("liveEntityCollection", discoveryPattern, liveAttributes);
    const discoveryAttr = discoveryPattern.at;
    const allAttrs = /* @__PURE__ */ new Set([discoveryAttr, ...liveAttributes]);
    const filtered = rollingFilter(thread, { at: anyOf(...allAttrs) });
    const isDiscoveryMatch = makeFilter(discoveryPattern);
    const attrSet = new Set(liveAttributes);
    const key = resolveKeyMapper(opts);
    const map = /* @__PURE__ */ new Map();
    function makeRecord(entityId) {
      const record = {};
      for (const attr of liveAttributes) record[key(attr)] = null;
      for (const log of filtered.applogs) {
        if (log.en === entityId && attrSet.has(log.at)) {
          record[key(log.at)] = log.vl;
        }
      }
      return record;
    }
    function buildFull(applogs) {
      map.clear();
      for (const log of isDiscoveryMatch(applogs)) {
        if (!map.has(log.en)) map.set(log.en, makeRecord(log.en));
      }
    }
    function addLog(log) {
      if (isDiscoveryMatch([log]).length > 0 && !map.has(log.en)) {
        map.set(log.en, makeRecord(log.en));
        return;
      }
      if (attrSet.has(log.at)) {
        const record = map.get(log.en);
        if (record) record[key(log.at)] = log.vl;
      }
    }
    function removeLog(log) {
      if (isDiscoveryMatch([log]).length > 0) {
        const stillDiscovered = filtered.applogs.some(
          (l) => l.en === log.en && isDiscoveryMatch([l]).length > 0
        );
        if (!stillDiscovered) {
          map.delete(log.en);
          return;
        }
      }
      if (attrSet.has(log.at)) {
        const record = map.get(log.en);
        if (record) {
          const current = filtered.applogs.find((l) => l.en === log.en && l.at === log.at);
          record[key(log.at)] = current?.vl ?? null;
        }
      }
    }
    buildFull(filtered.applogs);
    const result = new SubscribableImpl(
      map,
      () => filtered.subscribe((event) => {
        if (isInitEvent(event)) {
          buildFull(event.init);
        } else {
          if (event.removed) for (const log of event.removed) removeLog(log);
          for (const log of event.added) addLog(log);
        }
        result._set(map);
      }, "derived"),
      { equals: false }
    );
    return result;
  }
);

// src/query/epoch-snapshot.ts
import { addDays, addHours, addMinutes, addSeconds, subDays, subHours, subMinutes, subSeconds } from "date-fns";
function createEpochSnapshot(thread, options) {
  const { start, end } = options;
  if (isoDateStrCompare(start, end, "asc") >= 0) {
    throw new RangeError(
      `[createEpochSnapshot] Empty time range: start (${start}) >= end (${end})`
    );
  }
  const groups = /* @__PURE__ */ Object.create(null);
  for (const log of thread.applogs) {
    if (log.ts < start || log.ts >= end) continue;
    let bucket = groups[log.at];
    if (!bucket) {
      bucket = [];
      groups[log.at] = bucket;
    }
    bucket.push(log);
  }
  const keys = Object.keys(groups);
  for (let i = 0; i < keys.length; i++) {
    const attr = keys[i];
    groups[attr].sort((a, b) => isoDateStrCompare(a.ts, b.ts, "asc"));
  }
  const map = /* @__PURE__ */ new Map();
  for (let i = 0; i < keys.length; i++) {
    const attr = keys[i];
    map.set(attr, groups[attr]);
  }
  return map;
}
function resolveTimeSpec(spec, thread, now) {
  if (typeof spec === "number") {
    if (spec === -1) {
      if (thread.applogs.length === 0) return now.toISOString();
      return thread.applogs[0].ts;
    }
    if (spec === 0) return now.toISOString();
    return new Date(spec).toISOString();
  }
  if (spec === "now") return now.toISOString();
  const relMatch = spec.match(/^([+-]?\d+)([dhms])$/);
  if (relMatch) {
    const amount = parseInt(relMatch[1], 10);
    const unit = relMatch[2];
    let result;
    if (amount < 0) {
      const abs = Math.abs(amount);
      switch (unit) {
        case "d":
          result = subDays(now, abs);
          break;
        case "h":
          result = subHours(now, abs);
          break;
        case "m":
          result = subMinutes(now, abs);
          break;
        case "s":
          result = subSeconds(now, abs);
          break;
        default:
          result = now;
      }
    } else {
      switch (unit) {
        case "d":
          result = addDays(now, amount);
          break;
        case "h":
          result = addHours(now, amount);
          break;
        case "m":
          result = addMinutes(now, amount);
          break;
        case "s":
          result = addSeconds(now, amount);
          break;
        default:
          result = now;
      }
    }
    return result.toISOString();
  }
  return spec;
}
function serializeEpochSnapshot(snapshot, metadata) {
  const groups = {};
  let logCount = 0;
  for (const [attr, logs] of snapshot) {
    groups[attr] = logs;
    logCount += logs.length;
  }
  return {
    format: "wovin-epoch-snapshot-v1",
    attribute: metadata.attribute,
    timeRange: metadata.timeRange,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    logCount,
    groups
  };
}
async function purgeHistory(thread, attribute, start, end, options) {
  const now = options?.now ?? /* @__PURE__ */ new Date();
  const resolvedStart = resolveTimeSpec(start, thread, now);
  const resolvedEnd = resolveTimeSpec(end, thread, now);
  const timeRange = { start: resolvedStart, end: resolvedEnd };
  const snapshot = createEpochSnapshot(thread, timeRange);
  if (options?.persistSnapshot) {
    const serialized = serializeEpochSnapshot(snapshot, {
      attribute,
      timeRange
    });
    await options.persistSnapshot(serialized);
  }
  const targetLogs = snapshot.get(attribute);
  if (!targetLogs || targetLogs.length === 0) {
    return { snapshot, purgedCount: 0, keptCount: 0, timeRange };
  }
  const byEntity = /* @__PURE__ */ new Map();
  for (const log of targetLogs) {
    let bucket = byEntity.get(log.en);
    if (!bucket) {
      bucket = [];
      byEntity.set(log.en, bucket);
    }
    bucket.push(log);
  }
  const cidsToPurge = [];
  let keptCount = 0;
  for (const [, logs] of byEntity) {
    if (logs.length <= 1) {
      keptCount += logs.length;
      continue;
    }
    const winner = logs[logs.length - 1];
    keptCount += 1;
    for (let i = 0; i < logs.length - 1; i++) {
      cidsToPurge.push(logs[i].cid);
    }
  }
  const purgedCount = thread.purge(cidsToPurge);
  return { snapshot, purgedCount, keptCount, timeRange };
}

export {
  queryDivergencesByPrev,
  liveEntityCollection,
  createEpochSnapshot,
  resolveTimeSpec,
  serializeEpochSnapshot,
  purgeHistory
};
//# sourceMappingURL=chunk-OCDJRHXN.js.map